import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ModifyBookingService from './ModifyBookingService';
import SimpleWorkflowDisplay from './SimpleWorkflowDisplay';
import SimpleWorkflowModal from '../SimpleWorkflowModal';

interface ServiceWithWorkflowToggleProps {
  service: any;
  bookingStatus: string;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  showSalesInfo: boolean;
  canEdit: boolean;
  bookingId?: string;
}

export default function ServiceWithWorkflowToggle({
  service,
  bookingStatus,
  onUpdate,
  onError,
  showSalesInfo,
  canEdit,
  bookingId
}: ServiceWithWorkflowToggleProps) {
  // Check if service has workflow steps
  const hasWorkflow = service.workflow_steps && Array.isArray(service.workflow_steps) && service.workflow_steps.length > 0;
  
  // State for workflow template and assignment modal
  const [hasWorkflowTemplate, setHasWorkflowTemplate] = useState(false);
  const [isSimpleWorkflowModalOpen, setIsSimpleWorkflowModalOpen] = useState(false);
  const [currentServiceForWorkflow, setCurrentServiceForWorkflow] = useState<any>(null);
  
  // Default to 'workflow' if service has workflow, otherwise 'details'
  const [activeView, setActiveView] = useState<'details' | 'workflow'>(hasWorkflow ? 'workflow' : 'details');

  // Check if service has a workflow template but no assigned workflow
  useEffect(() => {
    const checkWorkflowTemplate = async () => {
      if (!hasWorkflow && service.service_id) {
        try {
          const { data: workflowTemplate } = await supabase
            .from('service_workflows')
            .select('id')
            .eq('service_id', service.service_id)
            .single();
          
          setHasWorkflowTemplate(!!workflowTemplate);
        } catch (error) {
          // No workflow template found
          setHasWorkflowTemplate(false);
        }
      } else {
        setHasWorkflowTemplate(false);
      }
    };

    checkWorkflowTemplate();
  }, [service, hasWorkflow]);

  // Handle assign workflow button click
  const handleAssignWorkflow = async () => {
    try {
      console.log('🔍 Assigning workflow for service:', service);
      
      // Fetch the workflow template for this service
      const { data: workflowTemplate } = await supabase
        .from('service_workflows')
        .select('*')
        .eq('service_id', service.service_id)
        .single();

      if (workflowTemplate) {
        console.log('📋 Found workflow template:', workflowTemplate);
        setCurrentServiceForWorkflow({
          id: service.service_id,
          name: service.name || service.service_name || 'Service',
          service_id: service.service_id,
          workflowTemplate
        });
        setIsSimpleWorkflowModalOpen(true);
      } else {
        onError('No workflow template found for this service');
      }
    } catch (error) {
      console.error('Error fetching workflow template:', error);
      onError('Failed to load workflow template');
    }
  };

  // Handle workflow assignment completion
  const handleWorkflowContinue = async (workflowSteps: any[]) => {
    try {
      console.log('🎯 Assigning workflow to existing booking service:', {
        serviceId: service.id,
        workflowSteps
      });

      // Update the booking_services table with the workflow_steps JSONB data
      const { error } = await supabase
        .from('booking_services')
        .update({ workflow_steps: workflowSteps })
        .eq('id', service.id);

      if (error) throw error;

      console.log('✅ Workflow assigned successfully');
      
      // Close modal and refresh the booking
      setIsSimpleWorkflowModalOpen(false);
      setCurrentServiceForWorkflow(null);
      await onUpdate();
    } catch (error) {
      console.error('❌ Error assigning workflow:', error);
      onError('Failed to assign workflow');
    }
  };

  // Handle workflow skip (just close modal)
  const handleWorkflowSkip = () => {
    setIsSimpleWorkflowModalOpen(false);
    setCurrentServiceForWorkflow(null);
  };

  return (
    <div className="px-4 py-3 relative">
      {/* Toggle Switch - Only show if service has workflow */}
      {hasWorkflow && (
        <div className="absolute top-3 left-4 z-10">
          <div className="flex items-center bg-gray-100 rounded-full p-1 shadow-sm">
            <button
              onClick={() => setActiveView('workflow')}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                activeView === 'workflow'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'bg-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Workflow
            </button>
            <button
              onClick={() => setActiveView('details')}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                activeView === 'details'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'bg-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Details
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
      {activeView === 'details' ? (
        <div className="pt-12"> {/* Add more top padding to account for toggle */}
          <ModifyBookingService
            service={service}
            bookingId={service.booking_id}
            onUpdate={onUpdate}
            onError={onError}
            showSalesInfo={showSalesInfo}
            canEdit={canEdit}
          />
          
          {/* Assign Workflow Button - Show if service has template but no workflow */}
          {hasWorkflowTemplate && canEdit && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Workflow Available</h4>
                  <p className="text-xs text-blue-700 mt-1">This service has a workflow template that can be assigned.</p>
                </div>
                <button
                  onClick={handleAssignWorkflow}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Assign Workflow
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="pt-12"> {/* Add more top padding to account for toggle */}
          <SimpleWorkflowDisplay
            service={service}
            bookingStatus={bookingStatus}
            onUpdate={onUpdate}
            onError={onError}
            bookingId={bookingId}
          />
        </div>
      )}

      {/* SimpleWorkflowModal - Reused from CreateBookingModal */}
      {isSimpleWorkflowModalOpen && currentServiceForWorkflow && (
        <SimpleWorkflowModal
          isOpen={isSimpleWorkflowModalOpen}
          onClose={handleWorkflowSkip}
          serviceName={currentServiceForWorkflow.name}
          serviceId={currentServiceForWorkflow.service_id}
          onSkip={handleWorkflowSkip}
          onContinue={handleWorkflowContinue}
          isEditing={false}
          existingWorkflowData={[]}
        />
      )}
    </div>
  );
}
